<template>
  <div class="fill-height">
    <!-- TODO: Move to seperate route page, and validate maintenance mode in routerBeforeEach helper -->
    <maintenance-page
      v-if="systemStatus.maintenanceIsActive"
      :maintenance-mode-content="systemStatus.content.MaintenanceMode"
    />

    <takeover-message-page
      v-if="fullPageMessage.show"
      :full-page-message="fullPageMessage"
    />

    <div
      v-if="!middlewareErrorOccurred && !systemStatus.maintenanceIsActive"
      v-show="!fullPageMessage.show"
      class="fill-height"
    >
      <div class="cf-notifications">
        <div class="cf-notifications__wrapper">
          <transition-group
            name="notification"
            tag="div"
            class="cf-notification-transition-container"
          >
            <cf-app-notification
              v-for="notification in notifications"
              :key="notification.id"
              :notification="notification"
              @close-notification="dismissNotification"
            />
          </transition-group>

          <cf-loyalty-notification />
        </div>
      </div>

      <!-- Router View -->
      <router-view />
    </div>

    <portal-modal-target />

    <app-modals />

    <!-- Svg-icon container -->
    <svg-icon-library />

    <!-- BlueSnap encryption div target -->
    <div id="bluesnap" />
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';
  import { v4 as uuid } from 'uuid';

  import errorHandlers from '@/services/error-handlers';
  import events from '@/services/events';

  import returnFullPageMessage from '@/helpers/return-full-page-message';
  import returnAppConfig from '@/helpers/return-app-config';

  import PortalModalTarget from '../components/portals/portal-modal-target/index.vue';
  import AppModals from '../components/modals/index.vue';

  import CfAppNotification from '../components/notifications/cf-app-notification/index.vue';
  import CfLoyaltyNotification from '../components/notifications/cf-loyalty-notification/index.vue';

  import MaintenancePage from '@/view/pages/shared/maintenance-page/index.vue';
  import TakeoverMessagePage from '@/view/pages/shared/takeover-message-page/index.vue';


  export default {
    name: 'Layout',

    components: {
      PortalModalTarget,
      AppModals,
      CfAppNotification,
      CfLoyaltyNotification,
      MaintenancePage,
      TakeoverMessagePage
    },

    data() {
      return {
        confirmationActionIsProcessing: false,
        fullPageMessage: {
          button: {
            action: null,
            isProcessing: false,
            show: false,
            text: null
          },
          description: null,
          show: false,
          subtitle: null,
          title: null
        },
        middlewareErrorOccurred: false,
        notifications: []
      };
    },

    computed: {
      ...mapGetters('merchant', ['merchant', 'systemStatus']),
      ...mapGetters('orders', ['order']),
      ...mapGetters('session', ['orderDetails', 'session']),

      isOrderAheadRedesignApp() {
        return this.merchant.isOrderAheadApp;
      }
    },

    watch: {
      'merchant.isLoading': 'handleMerchantLoading'
    },

    created() {
      this.createdHandler();
    },

    methods: {
      calculateAppHeight() {
        document.documentElement.style.setProperty('--app-height', `${window.innerHeight}px`);
      },

      async confirmationModalConfirmedHandler(data) {
        try {
          this.confirmationActionIsProcessing = true;
          await data.onConfirm();

          if (data.onConfirmSuccess) {
            data.onConfirmSuccess();
          }

          this.$closeModal('confirmation');
        }

        catch (error) {
          errorHandlers.generic(error);
        }

        finally {
          this.confirmationActionIsProcessing = false;
        }
      },

      createdHandler() {
        window.addEventListener('resize', this.calculateAppHeight);

        events.on('error', this.errorHandler);
        events.on('full-page-message', this.displayFullPageMessage);
        events.on('full-page-message:button-is-processing', this.updateFullPageMessageButtonIsProcessing);
        events.on('notification', this.displayNotification);
        events.on('ordering-error', this.orderingErrorHandler);
        events.on('unauthorized', this.handleUnauthorized);

        this.calculateAppHeight();
        this.detectTouchDevice();
        this.displayMiddlewareError();
      },

      detectTouchDevice() {
        const isTouchDevice = 'ontouchstart' in window;

        if (isTouchDevice) {
          document.body.classList.add('touch-device');
        }
      },

      displayFullPageMessage(event) {
        const message = returnFullPageMessage(event, this.merchant, this.session.route?.channelTypeId);

        this.fullPageMessage.button.action = message.button.action;
        this.fullPageMessage.button.isProcessing = false;
        this.fullPageMessage.button.show = message.button.show;
        this.fullPageMessage.button.text = message.button.text;
        this.fullPageMessage.description = message.description;
        this.fullPageMessage.subtitle = message.subtitle;
        this.fullPageMessage.title = message.title;

        this.fullPageMessage.show = true;
      },

      displayMiddlewareError() {
        const error = window._error;

        if (error) {
          this.middlewareErrorOccurred = true;

          errorHandlers.critical(new Error('Merchant info failed to load.'));
        }
      },

      dismissNotification(notification) {
        this.notifications = this.notifications.filter(n => n.id !== notification.id);
      },

      displayNotification(notification) {
        notification.id = uuid();

        if (notification.clickToDismiss) {
          const existingNotification = this.notifications.find(n => n.message === notification.message);

          if (!existingNotification) {
            this.notifications.push(notification);
          }

          setTimeout(() => {
            this.dismissNotification(notification);
          }, 10000);
        }

        else {
          this.notifications.push(notification);

          setTimeout(() => {
            this.dismissNotification(notification);
          }, notification.duration || 5000);
        }
      },

      errorHandler(data) {
        this.$closeAllModals();
        this.$openModal('error', data);
      },

      handleMerchantLoading(newVal) {
        const isFinishedLoading = !newVal;
        if (isFinishedLoading) this.validateBaseUrl();
      },

      handleUnauthorized() {
        this.$store.dispatch('session/signOut');

        if (this.$route.fullPath.indexOf('/order') > -1) {
          this.$router.push({
            name: 'order',
            params: {
              merchantId: this.$route.params.merchantId
            }
          });

          this.$openModal('signIn', {
            sessionExpired: true
          });
        }
      },

      orderingErrorHandler(data = {}) {
        this.$closeAllModals();
        this.$openModal('orderingError', data);
        this.$store.dispatch('session/unsetOrder');

        if (['pay', 'table'].includes(this.orderDetails.orderType)) {
          this.$router.push({
            name: 'welcome',
            params: {
              merchantId: this.$route.params.merchantId
            },
            query: {
              merchantLocationId: this.session.route.merchantLocationId
            }
          });
        }

        else {
          this.$router.push({
            name: 'order',
            params: {
              merchantId: this.$route.params.merchantId
            }
          });
        }
      },

      updateFullPageMessageButtonIsProcessing(isProcessing) {
        this.fullPageMessage.button.isProcessing = isProcessing;
      },

      validateBaseUrl() {
        const { isLocalEnvironment } = returnAppConfig();
        const { location } = window;
        const { baseUrl: merchantBaseUrl } = this.merchant.options;
        const currentBaseUrl = `${location.origin}/`; /* we include the trailing slash on our baseUrl */
        const hasValidBaseUrl = merchantBaseUrl && merchantBaseUrl[merchantBaseUrl.length - 1] === '/';

        if (!isLocalEnvironment && hasValidBaseUrl && merchantBaseUrl !== currentBaseUrl) {
          const newUrl = location.href.replace(currentBaseUrl, merchantBaseUrl);
          location.replace(newUrl);
        }
      }
    }
  };
</script>


<style lang="sass">
  @import '../../../styles/main'
</style>
